/**
 * Internal API contract (spec section 65).
 *
 * This file is the seam between apps/api and apps/web. Both import these
 * schemas, so a route that changes shape breaks the frontend typecheck rather
 * than only breaking at runtime.
 */

import { z } from 'zod';

// ---------------------------------------------------------------------------
// Shared envelopes
// ---------------------------------------------------------------------------

export const confidenceLevelSchema = z.enum(['HIGH', 'MEDIUM', 'LOW', 'UNKNOWN']);
export const dataQualitySchema = z.enum(['CONFIRMED', 'POSSIBLE', 'UNKNOWN', 'STALE']);
export const freshnessBucketSchema = z.enum(['FRESH', 'RECENT', 'AGING', 'STALE', 'UNKNOWN']);

export const freshnessInfoSchema = z.object({
  bucket: freshnessBucketSchema,
  ageSeconds: z.number().nullable(),
  label: z.string(),
});

/**
 * Spec sections 1.1 and 18: a recommendation without a model version, source
 * timestamp, confidence, and explanation is not a valid recommendation. All of
 * those are required here so the type system enforces it.
 */
export function recommendationSchema<T extends z.ZodTypeAny>(result: T) {
  return z.object({
    result,
    confidence: z.number().min(0).max(1),
    confidenceLevel: confidenceLevelSchema,
    reasons: z.array(z.string()),
    warnings: z.array(z.string()),
    modelVersion: z.string(),
    ruleVersion: z.string().optional(),
    sourceDataAt: z.string(),
  });
}

export const scoreBreakdownSchema = z.object({
  score: z.number(),
  confidence: z.number().min(0).max(1),
  confidenceLevel: confidenceLevelSchema,
  components: z.record(z.number()),
  warnings: z.array(z.string()),
});

export const apiErrorSchema = z.object({
  error: z.object({
    kind: z.string(),
    message: z.string(),
    status: z.number().nullable().optional(),
  }),
});

// ---------------------------------------------------------------------------
// Players
// ---------------------------------------------------------------------------

export const playerSummarySchema = z.object({
  id: z.string(),
  name: z.string().nullable(),
  avatarUrl: z.string().nullable(),
  kingdomId: z.number().nullable(),
  townHallLevel: z.number().nullable(),
  power: z.number().nullable(),
  level: z.number().nullable(),
  vip: z.number().nullable(),
  kills: z.number().nullable(),
  mysticTrialScore: z.number().nullable(),
  allianceId: z.string().nullable(),
  allianceAbbr: z.string().nullable(),
  allianceName: z.string().nullable(),
  active: z.boolean().nullable(),

  /**
   * Per-field quality. Spec section 56: a Jeabs `vip: 0` may mean "VIP 0" or
   * "not enriched yet", and the UI must be able to tell the difference.
   */
  quality: z.record(dataQualitySchema),

  /** Spec section 64: separate clocks, never one global "last updated". */
  freshness: z.object({
    profile: freshnessInfoSchema,
    kills: freshnessInfoSchema,
    standings: freshnessInfoSchema,
    heroGear: freshnessInfoSchema,
  }),
});

export const playerHistoryPointSchema = z.object({
  capturedAt: z.string(),
  power: z.number().nullable(),
  townHallLevel: z.number().nullable(),
  kills: z.number().nullable(),
  allianceId: z.string().nullable(),
});

export const playerGrowthSchema = z.object({
  playerId: z.string(),
  modelVersion: z.string(),
  powerGrowth24h: z.number().nullable(),
  powerGrowth7d: z.number().nullable(),
  powerGrowth30d: z.number().nullable(),
  powerGrowthPct24h: z.number().nullable(),
  powerGrowthPct7d: z.number().nullable(),
  powerPercentileAlliance: z.number().nullable(),
  powerPercentileKingdom: z.number().nullable(),
  growthFlag: z.enum(['WHALE', 'FAST_GROWER', 'RISING', 'NORMAL', 'DORMANT']).nullable(),
  sampleCount: z.number(),
  confidence: z.number().nullable(),
  warnings: z.array(z.string()),
});

/** Hero view. Note `starStage`, never `stars` -- the encoding is unverified. */
export const playerHeroSchema = z.object({
  heroId: z.number(),
  /** Null when the ID is not yet in hero_catalog. The UI shows the raw ID. */
  name: z.string().nullable(),
  catalogVerified: z.boolean(),
  slot: z.number().nullable(),
  position: z.number().nullable(),
  level: z.number().nullable(),
  starStage: z.number().nullable(),
  exclusiveEquipId: z.string().nullable(),
  exclusiveEquipLevel: z.number().nullable(),
  skills: z.array(
    z.object({
      skillId: z.number(),
      name: z.string().nullable(),
      level: z.number().nullable(),
      /** Display ordering only. Carries no verified meaning (spec 8.3). */
      displayIndex: z.number().nullable(),
    }),
  ),
  /** Keyed and ordered by slotId (Jeabs `sid`), never by array position. */
  equipment: z.array(
    z.object({
      equipmentId: z.string(),
      slotId: z.number(),
      enhancementLevel: z.number().nullable(),
      enhancementExp: z.number().nullable(),
      refinementLevel: z.number().nullable(),
      catalogVerified: z.boolean(),
    }),
  ),
});

export const playerHeroesResponseSchema = z.object({
  playerId: z.string(),
  heroes: z.array(playerHeroSchema),
  /** Opaque Jeabs stats, surfaced raw with candidate labels only. */
  opaqueStats: z.array(
    z.object({
      key: z.string(),
      value: z.number(),
      candidateName: z.string().nullable(),
      verified: z.boolean(),
      confidence: z.number().nullable(),
    }),
  ),
  /** Spec 74: the hero list may not be the complete roster. */
  rosterCompleteness: z.enum(['UNVERIFIED', 'ASSUMED_COMPLETE', 'KNOWN_PARTIAL']),
  freshness: freshnessInfoSchema,
  warnings: z.array(z.string()),
});

// ---------------------------------------------------------------------------
// Alliance / state intel
// ---------------------------------------------------------------------------

export const allianceSummarySchema = z.object({
  id: z.string(),
  kingdomId: z.number().nullable(),
  abbr: z.string().nullable(),
  name: z.string().nullable(),
  level: z.number().nullable(),
  power: z.number().nullable(),
  kills: z.number().nullable(),
  memberCount: z.number().nullable(),
  memberMax: z.number().nullable(),
  leaderName: z.string().nullable(),
  freshness: freshnessInfoSchema,
});

export const allianceGrowthSchema = z.object({
  allianceId: z.string(),
  powerGrowth24h: z.number().nullable(),
  powerGrowth7d: z.number().nullable(),
  powerGrowth30d: z.number().nullable(),
  powerGrowthPct7d: z.number().nullable(),
  memberDelta7d: z.number().nullable(),
  sampleCount: z.number(),
  confidence: z.number().nullable(),
});

export const detectedEventSchema = z.object({
  id: z.string(),
  eventType: z.enum([
    'PLAYER_JOINED_ALLIANCE',
    'PLAYER_LEFT_ALLIANCE',
    'PLAYER_TRANSFERRED_ALLIANCE',
    'PLAYER_RENAMED',
    'TC_UPGRADE',
    'POWER_SPIKE',
    'PLAYER_DORMANT',
    'PLAYER_REACTIVATED',
  ]),
  playerId: z.string().nullable(),
  playerName: z.string().nullable(),
  allianceId: z.string().nullable(),
  relatedAllianceId: z.string().nullable(),
  detectedAt: z.string(),
  confidence: z.number().nullable(),
  payload: z.record(z.unknown()),
});

export const dashboardResponseSchema = z.object({
  coreAlliance: allianceSummarySchema.nullable(),
  allianceGrowth: allianceGrowthSchema.nullable(),
  stateRank: z.number().nullable(),
  movers: z.array(
    z.object({
      playerId: z.string(),
      playerName: z.string().nullable(),
      powerDelta: z.number().nullable(),
      tcFrom: z.number().nullable(),
      tcTo: z.number().nullable(),
    }),
  ),
  recentActivity: z.array(detectedEventSchema),
  upcomingEvents: z.array(
    z.object({
      id: z.string(),
      eventType: z.string(),
      name: z.string().nullable(),
      startsAt: z.string().nullable(),
      status: z.string(),
    }),
  ),
  /**
   * Spec section 58: surfaced problems, e.g. "Bear lead data stale for 2".
   *
   * `code` and `params` exist so the UI can localize these. A server-generated
   * English sentence cannot be translated by the frontend, which left the most
   * prominent banner on the dashboard stuck in English for a Korean operator.
   * `message` remains as the English fallback for an unrecognized code.
   */
  warnings: z.array(
    z.object({
      severity: z.enum(['INFO', 'WARN', 'CRITICAL']),
      code: z.enum([
        'NO_CORE_ALLIANCE',
        'CORE_ALLIANCE_NEVER_SYNCED',
        'CORE_ROSTER_STALE',
        'API_BUDGET',
        'SYNC_FAILURES',
        'UNKNOWN',
      ]),
      params: z.record(z.union([z.string(), z.number()])),
      message: z.string(),
    }),
  ),
});

// ---------------------------------------------------------------------------
// Bear
// ---------------------------------------------------------------------------

const troopCountsSchema = z.object({
  infantry: z.number().int().min(0).nullable(),
  cavalry: z.number().int().min(0).nullable(),
  archer: z.number().int().min(0).nullable(),
});

export const createBearSessionSchema = z.object({
  allianceId: z.string().optional(),
  eventId: z.string().uuid().optional(),
  trapNumber: z.number().int().optional(),
  startedAt: z.string().optional(),
  endedAt: z.string().optional(),
  allianceTotalDamage: z.number().int().min(0).optional(),
  participantCount: z.number().int().min(0).optional(),
  gameNotes: z.string().optional(),
});

export const createBearRallySchema = z.object({
  sequence: z.number().int().optional(),
  wave: z.number().int().optional(),
  leaderPlayerId: z.string(),
  openedAt: z.string().optional(),
  departedAt: z.string().optional(),
  returnedAt: z.string().optional(),
  leaderHeroIds: z.array(z.number().int()).max(3).optional(),
  rallyCapacity: z.number().int().min(0).optional(),
  actualTroops: z.number().int().min(0).optional(),
  totalDamage: z.number().int().min(0).optional(),
  /** R4 bulk entry: the whole rally including members in one request. */
  members: z
    .array(
      z.object({
        playerId: z.string(),
        joinOrder: z.number().int().optional(),
        captainHeroId: z.number().int().optional(),
        troops: troopCountsSchema.partial().optional(),
        totalTroops: z.number().int().min(0).optional(),
        damage: z.number().int().min(0).optional(),
        departedAt: z.string().optional(),
        returnedAt: z.string().optional(),
      }),
    )
    .optional(),
});

export const bearBreakdownRowSchema = z.object({
  key: z.string(),
  label: z.string(),
  samples: z.number(),
  mean: z.number(),
  median: z.number(),
  best: z.number(),
  normalized: z.number(),
});

const bearSideSchema = z.object({
  samples: z.number(),
  average: z.number().nullable(),
  median: z.number().nullable(),
  best: z.number().nullable(),
  damagePerTroop: z.number().nullable(),
});

const bearTrendSchema = z.object({
  sessions: z.number(),
  meanDamage: z.number().nullable(),
  medianDamage: z.number().nullable(),
  changeVsPrevious: z.number().nullable(),
});

export const personalBearAnalysisSchema = z.object({
  playerId: z.string(),
  playerName: z.string().nullable(),
  modelVersion: z.string(),
  lead: bearSideSchema,
  join: bearSideSchema,
  leadVsJoinAdvantage: z.number().nullable(),
  recommendedRole: z.enum(['LEAD', 'JOIN', 'MIXED', 'INSUFFICIENT_DATA']),
  bestJoinTargets: z.array(bearBreakdownRowSchema),
  byCaptainHero: z.array(bearBreakdownRowSchema),
  byFormation: z.array(bearBreakdownRowSchema),
  byWave: z.array(bearBreakdownRowSchema),
  trends: z.object({ last3: bearTrendSchema, last5: bearTrendSchema, last10: bearTrendSchema }),
  returnCycleTimeSeconds: z.number().nullable(),
  upgradeImpacts: z.array(
    z.object({
      description: z.string(),
      changedAt: z.string(),
      beforeMedian: z.number().nullable(),
      afterMedian: z.number().nullable(),
      beforeSamples: z.number(),
      afterSamples: z.number(),
      estimatedChange: z.number().nullable(),
      confidenceLevel: confidenceLevelSchema,
      /** Non-empty means the UI must not present this as a cause. */
      confoundedBy: z.array(z.string()),
    }),
  ),
  confidence: z.number(),
  confidenceLevel: confidenceLevelSchema,
  reasons: z.array(z.string()),
  warnings: z.array(z.string()),
  sourceDataAt: z.string(),
});

/**
 * One row in the Bear session history list.
 *
 * Counts come from the session's recorded rallies rather than being trusted
 * from a stored total, so a half-entered session reads as half-entered instead
 * of looking complete. `entryStatus` is what tells an operator whether the
 * numbers below are final.
 */
export const bearSessionSummarySchema = z.object({
  id: z.string(),
  allianceId: z.string().nullable(),
  trapNumber: z.number().nullable(),
  startedAt: z.string().nullable(),
  endedAt: z.string().nullable(),
  allianceTotalDamage: z.number().nullable(),
  participantCount: z.number().nullable(),
  rallyCount: z.number(),
  recordedMemberCount: z.number(),
  /** DRAFT means data entry is still in progress; totals may be partial. */
  entryStatus: z.enum(['DRAFT', 'COMPLETE']),
  gameNotes: z.string().nullable(),
  createdAt: z.string(),
});

export const bearSessionListResponseSchema = z.object({
  sessions: z.array(bearSessionSummarySchema),
  total: z.number(),
});

/** Full detail for one session, used by the history drill-down. */
export const bearSessionDetailResponseSchema = z.object({
  session: bearSessionSummarySchema,
  rallies: z.array(
    z.object({
      id: z.string(),
      sequence: z.number().nullable(),
      wave: z.number().nullable(),
      leaderPlayerId: z.string(),
      leaderPlayerName: z.string().nullable(),
      openedAt: z.string().nullable(),
      departedAt: z.string().nullable(),
      returnedAt: z.string().nullable(),
      leaderHeroIds: z.array(z.number()),
      rallyCapacity: z.number().nullable(),
      actualTroops: z.number().nullable(),
      totalDamage: z.number().nullable(),
      members: z.array(
        z.object({
          playerId: z.string(),
          playerName: z.string().nullable(),
          joinOrder: z.number().nullable(),
          captainHeroId: z.number().nullable(),
          infantry: z.number().nullable(),
          cavalry: z.number().nullable(),
          archer: z.number().nullable(),
          totalTroops: z.number().nullable(),
          damage: z.number().nullable(),
          /** Spec section 25: how far the bound gear snapshot sits from the rally. */
          contextQuality: z.enum(['EXACT', 'NEAR', 'STALE', 'MISSING']).nullable(),
          contextAgeSeconds: z.number().nullable(),
        }),
      ),
    }),
  ),
  warnings: z.array(z.string()),
});

export const bearLeadCandidateSchema = z.object({
  playerId: z.string(),
  playerName: z.string().nullable(),
  leadScore: z.number(),
  confidence: z.number(),
  confidenceLevel: confidenceLevelSchema,
  observedAvgDamage: z.number().nullable(),
  samples: z.number(),
  recommendedWave: z.number().nullable(),
  reasons: z.array(z.string()),
  warnings: z.array(z.string()),
  components: z.record(z.number()),
});

export const bearPlanRequestSchema = z.object({
  expectedParticipants: z.array(z.string()).min(1),
  availableMarchQueues: z.record(z.number().int().min(0)).optional(),
  troopAvailability: z.record(troopCountsSchema.partial()).optional(),
  manualLeadLocks: z.array(z.string()).optional(),
  manualDoNotLead: z.array(z.string()).optional(),
  plannerMode: z.enum(['MAX_ALLIANCE_DAMAGE', 'BALANCED_REWARDS', 'PERSONAL_SCORE_ASSIST']),
  focusPlayerId: z.string().optional(),
  waveCount: z.number().int().min(1).max(10).optional(),
  ralliesPerWave: z.number().int().min(1).max(20).optional(),
});

export const bearPlanResponseSchema = z.object({
  waves: z.array(
    z.object({
      wave: z.number(),
      rallies: z.array(
        z.object({
          leaderPlayerId: z.string(),
          leaderPlayerName: z.string().nullable(),
          expectedOpenAt: z.string().optional(),
          preferredJoiners: z.array(
            z.object({ playerId: z.string(), playerName: z.string().nullable(), reason: z.string() }),
          ),
          backupLead: z.string().nullable(),
          expectedRelativeStrength: z.number().nullable(),
          explanation: z.array(z.string()),
        }),
      ),
    }),
  ),
  leadRanking: z.array(bearLeadCandidateSchema),
  assumptions: z.array(z.string()),
  warnings: z.array(z.string()),
  modelVersion: z.string(),
  ruleVersion: z.string().nullable(),
  confidence: z.number(),
  recommendationRunId: z.string().nullable(),
});

// ---------------------------------------------------------------------------
// Sync status (spec section 68)
// ---------------------------------------------------------------------------

export const syncStatusSchema = z.object({
  dailyRequestsUsed: z.number(),
  dailyRequestBudget: z.number(),
  rateLimitedToday: z.number(),
  lastKingdomSync: z.string().nullable(),
  lastAllianceSync: z.string().nullable(),
  queueDepth: z.number(),
  recentFailures: z.array(
    z.object({
      jobType: z.string(),
      resourceKey: z.string(),
      errorKind: z.string().nullable(),
      errorMessage: z.string().nullable(),
      finishedAt: z.string(),
    }),
  ),
  stalePlayers: z.array(
    z.object({ playerId: z.string(), playerName: z.string().nullable(), ageSeconds: z.number() }),
  ),
});

/**
 * A row of `strategy_rules` as the UI sees it (spec sections 1.2, 22, 74).
 *
 * `confidence` and `sourceUrl` travel with the config on purpose. A mechanic
 * this system holds at 0.35 from one guide site and one it holds at 0.87 from
 * official text are different kinds of claim, and an operator deciding whether
 * to act on a rule needs to see which one they are looking at.
 */
export const strategyRuleSchema = z.object({
  id: z.string(),
  eventType: z.string(),
  ruleKey: z.string(),
  version: z.string(),
  config: z.record(z.unknown()),
  sourceUrl: z.string().nullable(),
  sourceType: z.string().nullable(),
  /** Null means nobody has scored it. Distinct from 0, which means "refuted". */
  confidence: z.number().nullable(),
  verified: z.boolean(),
  notes: z.string().nullable(),
  effectiveFrom: z.string().nullable(),
  effectiveTo: z.string().nullable(),
  /** False when a newer version of the same rule supersedes this row. */
  active: z.boolean(),
});

export const strategyRulesResponseSchema = z.object({
  rules: z.array(strategyRuleSchema),
});

// ---------------------------------------------------------------------------
// Response envelopes
// ---------------------------------------------------------------------------
// Every list response is an OBJECT, never a bare array, so that pagination or
// a freshness stamp can be added later without breaking existing callers.

/** Who am I. Drives /bear/me and hides operator-only UI from viewers. */
export const meResponseSchema = z.object({
  userId: z.string(),
  role: z.enum(['R5', 'R4', 'MEMBER', 'VIEWER']),
  /**
   * The caller's own in-game account, from `app_users.player_id`. Null when an
   * operator has not linked the account yet -- the UI must handle that rather
   * than assuming /bear/me always resolves.
   */
  playerId: z.string().nullable(),
  displayName: z.string().nullable(),
});

export const playerListResponseSchema = z.object({
  players: z.array(playerSummarySchema),
  total: z.number(),
});

export const playerDetailResponseSchema = z.object({
  player: playerSummarySchema,
  /** Null until the metrics job has run for this player. */
  growth: playerGrowthSchema.nullable(),
});

export const playerHistoryResponseSchema = z.object({
  playerId: z.string(),
  points: z.array(playerHistoryPointSchema),
});

/**
 * Spec section 17 keeps event roles separate, and section 59 shows Bear Lead
 * and Bear Join alongside the PvP roles. Those live in different tables
 * (`player_capabilities` vs `rally_role_scores`), so `role` is an open string
 * and the server supplies the display `label`. Constraining it to RALLY_ROLES
 * would exclude the Bear scores the player screen is required to show.
 */
export const playerCapabilitiesResponseSchema = z.object({
  playerId: z.string(),
  modelVersion: z.string(),
  sourceDataAt: z.string().nullable(),
  capabilities: z.array(
    scoreBreakdownSchema.extend({
      role: z.string(),
      label: z.string(),
      samples: z.number(),
    }),
  ),
});

export const allianceDetailResponseSchema = z.object({
  alliance: allianceSummarySchema,
  growth: allianceGrowthSchema.nullable(),
});

export const allianceMembersResponseSchema = z.object({
  allianceId: z.string(),
  members: z.array(playerSummarySchema),
});

export const allianceActivityResponseSchema = z.object({
  events: z.array(detectedEventSchema),
});

export const stateAlliancesResponseSchema = z.object({
  kingdomId: z.number(),
  alliances: z.array(allianceSummarySchema),
});

export const stateTransfersResponseSchema = z.object({
  kingdomId: z.number(),
  transfers: z.array(detectedEventSchema),
});

export type MeResponse = z.infer<typeof meResponseSchema>;
export type PlayerListResponse = z.infer<typeof playerListResponseSchema>;
export type PlayerDetailResponse = z.infer<typeof playerDetailResponseSchema>;
export type PlayerHistoryResponse = z.infer<typeof playerHistoryResponseSchema>;
export type PlayerCapabilitiesResponse = z.infer<typeof playerCapabilitiesResponseSchema>;
export type AllianceDetailResponse = z.infer<typeof allianceDetailResponseSchema>;
export type AllianceMembersResponse = z.infer<typeof allianceMembersResponseSchema>;
export type AllianceActivityResponse = z.infer<typeof allianceActivityResponseSchema>;
export type StateAlliancesResponse = z.infer<typeof stateAlliancesResponseSchema>;
export type StateTransfersResponse = z.infer<typeof stateTransfersResponseSchema>;

export type PlayerSummary = z.infer<typeof playerSummarySchema>;
export type PlayerHeroesResponse = z.infer<typeof playerHeroesResponseSchema>;
export type AllianceSummary = z.infer<typeof allianceSummarySchema>;
export type DashboardResponse = z.infer<typeof dashboardResponseSchema>;
export type DetectedEventDto = z.infer<typeof detectedEventSchema>;
export type PersonalBearAnalysisDto = z.infer<typeof personalBearAnalysisSchema>;
export type BearLeadCandidateDto = z.infer<typeof bearLeadCandidateSchema>;
export type BearPlanRequest = z.infer<typeof bearPlanRequestSchema>;
export type BearPlanResponse = z.infer<typeof bearPlanResponseSchema>;
export type SyncStatus = z.infer<typeof syncStatusSchema>;
export type StrategyRule = z.infer<typeof strategyRuleSchema>;
export type StrategyRulesResponse = z.infer<typeof strategyRulesResponseSchema>;
export type BearSessionSummary = z.infer<typeof bearSessionSummarySchema>;
export type BearSessionListResponse = z.infer<typeof bearSessionListResponseSchema>;
export type BearSessionDetailResponse = z.infer<typeof bearSessionDetailResponseSchema>;
export type CreateBearSession = z.infer<typeof createBearSessionSchema>;
export type CreateBearRally = z.infer<typeof createBearRallySchema>;
export type PlayerGrowth = z.infer<typeof playerGrowthSchema>;
export type AllianceGrowth = z.infer<typeof allianceGrowthSchema>;
export type PlayerHistoryPoint = z.infer<typeof playerHistoryPointSchema>;
